import customHeaders, { getSpace } from '../../common/headers';
import { useEffect, useState, useRef } from 'react';
import {
  ButtonBasic,
  TableBox,
  DropDownSelector,
  RangeBox,
} from '@lk-gtcom/ecomlab-components';
import add_icon from './img/add.svg';
import { KonstantinMasterHttps } from '../../fetchUrls';
import { useDataTable } from '../../common/hooks';
import useGeneralStore from '../../store/general';
import './FinanceForServices.scss';
import AddBalanceModal from '../../components/AddBalanceModal';
import { TitlePage } from '../../components/TitlePage/TitlePage';
import { periodWeek } from '../../common/constant';
import { msToLocalDate } from '../../common/constant';
import { useURLSearchParams } from '../../common/useURLSearchParams';
import { useResponseHandler } from '../../common/auth';

const options_period = [
  { label: 'По дням', value: 'День' },
  { label: 'По неделям', value: 'Неделя' },
  { label: 'По месяцам', value: 'Месяц' },
];

const FinanceForServices = () => {
  const selectedSpace = useGeneralStore((state) => state.selectedSpace);
  const headers = { ...customHeaders, ...getSpace(selectedSpace) };
  const { handleTableResponse, handleRequestError } = useResponseHandler();
  const [selectedPeriod, setSelectedPeriod] = useState(options_period[0]);
  const [isModalBalance, setIsModalBalance] = useState(false);

  const [date, setDate] = useState(periodWeek());
  const [dateFrom, dateTo] = date;

  // Фильтры
  const filterConfig = [
    { key: 'type_operation', type: 'single' },
    { key: 'period', type: 'single' },
    { key: 'date_from', type: 'date' },
    { key: 'date_to', type: 'date' }
  ];
  const { urlParams, updateURL, isInitialized, hasInitialParams } = useURLSearchParams(filterConfig);
  const [isFilterRequest, setIsFilterRequest] = useState(false);
  const [operationType, setOpearationType] = useState([]);
  const [selectedOperationType, setSelectedOpearationType] = useState(null);

  // Таблица
  const [isTableRequest, setIsTableRequest] = useState(false);
  const paginatorRef = useRef();
  const [setTable, tableProps, setTableFuncs] = useDataTable(
    paginatorRef.current?.setTotal,
    paginatorRef
  );
  const { setLoading } = setTableFuncs;

  // Инициализация фильтров из URL параметров при первом рендере
  const [isInitialLoadFromURL, setIsInitialLoadFromURL] = useState(false);

  useEffect(() => {
    if (!isInitialLoadFromURL) {
      setIsInitialLoadFromURL(true);

      // Обновляем все состояния фильтров
      setTimeout(() => {
        if (urlParams.period) setSelectedPeriod(urlParams.period)
        if (urlParams.type_operation) setSelectedOpearationType(urlParams.type_operation)
        if (urlParams.date_from) {
          setDate([
            new Date(parseInt(urlParams.date_from)),
            new Date(parseInt(urlParams.date_to)),
          ]);
        }

        // Запускаем запрос таблицы и фильтров только при первой инициализации из URL
        setIsFilterRequest(prev => !prev);
        if (Object.keys(urlParams).length > 0) {
          setIsTableRequest(prev => !prev);
        }
      }, 10);
    }
  }, [urlParams]);

  useEffect(() => {
    updateURL({
      type_operation: urlParams?.type_operation || selectedOperationType,
      period: urlParams?.period || selectedPeriod,
      date_from: urlParams?.date_from || dateFrom,
      date_to: urlParams?.date_to || dateTo,
    })
  }, []);

  const fetchTableData = (params, abortController = new AbortController()) => {
    if (!selectedPeriod?.value) return
    let sort;
    let filters;

    if (params) {
      [sort, filters] = params;
    }

    const [date_from, date_to] = date;
    const orderParam = sort?.[0]
      ? `&order_param=${sort?.[0]}&order_param_direction=${sort?.[1]}`
      : '';
    const operationType = `&operation_type=${selectedOperationType?.value}`;

    setLoading(true);
    let url = `${KonstantinMasterHttps}/finance/by-services/details-by-service?period=${selectedPeriod?.value}${operationType}${orderParam}`;


    const body = JSON.stringify({
      date_from: msToLocalDate(date_from),
      date_to: msToLocalDate(date_to),
      filters,
      limit: paginatorRef.current?.limit,
      page: paginatorRef.current?.page,
    });

    fetch(url, { body, method: 'POST', headers, signal: abortController.signal })
      .then(handleTableResponse)
      .then((json) => setTable(json))
      .catch(handleRequestError)
      .finally(() => {
        if (abortController.signal.aborted) return;
        setLoading(false);
      });
  };

  const fetchOperationType = (abortController = new AbortController()) => {
    let url = `${KonstantinMasterHttps}/finance/by-services/operation-types`;

    const [date_from, date_to] = date;

    const body = JSON.stringify({
      date_from: msToLocalDate(date_from),
      date_to: msToLocalDate(date_to),
    });

    fetch(url, { body, method: 'POST', headers, signal: abortController.signal })
      .then(handleTableResponse)
      .then((json) => {
        setOpearationType(json);
        if (json?.[0]) {
          setSelectedOpearationType(json?.[0]);
          if (!selectedOperationType?.value) {
            updateURL({ operationType: json?.[0] });
          }
        }
      })
      .catch(handleRequestError)
      .finally(() => {
        if (abortController.signal.aborted) return;
      });
  };

  useEffect(() => {
    const abortController = new AbortController();
    fetchOperationType(abortController);
    return () => abortController.abort();
  }, [date])

  useEffect(() => {
    const abortController = new AbortController();
    fetchTableData([], abortController);
    return () => abortController.abort();
  }, [date, isTableRequest]);

  return (
    <div className="finance-for-services">
      {isModalBalance && (
        <AddBalanceModal setIsModalActive={() => setIsModalBalance(false)} />
      )}

      <div className="finance-for-services__header">
        <TitlePage
          title='По услугам'
        />
        <ButtonBasic
          green
          width="190px"
          icon={add_icon}
          text={'Пополнить баланс'}
          onClick={() => setIsModalBalance(true)}
        />
      </div>

      <div className="finance-for-services__table">
        <div className="finance-for-services__header-table">
          <DropDownSelector
            placeholder='Тип операции'
            options_prop={operationType}
            state={selectedOperationType}
            setState={(e) => setSelectedOpearationType(e)}
            onBlur={(e) => {
              updateURL({ type_operation: selectedOperationType });
              setIsTableRequest(!isTableRequest)
            }}
            isClearable={false}
          />

          <DropDownSelector
            options_prop={options_period}
            state={selectedPeriod}
            setState={(e) => setSelectedPeriod(e)}
            onBlur={(e) => {
              updateURL({ period: selectedPeriod });
              setIsTableRequest(!isTableRequest)
            }}
            isClearable={false}
            maxWidth='150px'
          />

          <RangeBox
            date={date}
            setDate={(e) => {
              const [newDateFrom, newDateTo] = e;
              updateURL({
                date_from: newDateFrom,
                date_to: newDateTo
              });
              setDate(e)
            }}
          />
        </div>

        <TableBox
          ref={paginatorRef}
          paginator={true}
          {...tableProps}
          fetchCallback={fetchTableData}
        />
      </div>
    </div>
  );
};

export { FinanceForServices };
